import {
  Fragment,
  useCallback,
  useEffect,
  useState,
  useMemo,
  FC,
  ReactNode,
  ChangeEventHandler,
} from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Box, Flex } from '@rebass/emotion';

import { APP_BASE_URL } from '@config/common.config';
import { isNotNullish } from '@app/helpers/predicate';
import useCart from '@app/shop/hooks/useCart';
import useShopStore from '@app/shop/hooks/useShopStore';
import useAlerts from '@app/shop/hooks/useAlerts';
import { RadioBoxLabel, RadioBoxRadio } from '@app/shop/components/RadioBox';
import {
  AlertTitle,
  AlertMessage,
  AlertType,
} from '@app/shop/components/Alert';
import Alerts from '@app/shop/components/Alerts';
import { Button } from '@app/shop/components/Button';
import CheckoutBackLink from '@app/shop/components/CheckoutBackLink';
import { H1 } from '@app/shop/components/typography';
import useGTMTracking from '@app/shop/hooks/useGTMTracking';

import PaymentOption from './PaymentOption/PaymentOption';
import {
  PAYMENT_METHODS_QUERY,
  SET_PAYMENT_METHOD_MUTATION,
  PLACE_ORDER_MUTATION,
} from './Payment.queries';
import messages from './Payment.messages';

import {
  PaymentMethodsQuery,
  PaymentMethodsQueryVariables,
} from './__generated__/PaymentMethodsQuery';
import {
  SetPaymentMethodMutation,
  SetPaymentMethodMutationVariables,
} from './__generated__/SetPaymentMethodMutation';
import {
  PlaceOrderMutation,
  PlaceOrderMutationVariables,
} from './__generated__/PlaceOrderMutation';

const Payment: FC = () => {
  const { language, status } = useParams<{
    language: string;
    status?: string;
  }>();
  const { storeCode } = useShopStore();
  const { cartId, isVirtual, items, prices, storeConfig } = useCart();
  const { alerts, addAlerts, clearAlerts } = useAlerts();
  const [selectedMethodCode, setSelectedMethodCode] = useState<
    string | undefined
  >();
  const [redirecting, setRedirecting] = useState(false);

  const { data, loading: methodsLoading } = useQuery<
    PaymentMethodsQuery,
    PaymentMethodsQueryVariables
  >(PAYMENT_METHODS_QUERY, {
    variables: { storeCode: storeCode || '', isVirtualCart: !!isVirtual },
    skip: !(storeCode && cartId),
    ssr: false,
    onError: (error) => {
      addAlerts([genericErrorAlert({ message: error.message })]);
    },
  });

  useGTMTracking({ step: 'payment', items, prices, storeConfig });

  const [placeOrder, { loading: placeOrderLoading }] = useMutation<
    PlaceOrderMutation,
    PlaceOrderMutationVariables
  >(PLACE_ORDER_MUTATION, {
    variables: {
      cartId: cartId ?? '',
      returnUrl: getReturnUrl(language),
      successUrl: getSuccessUrl(language, cartId ?? ''),
      failedUrl: getFailedUrl(language),
    },
    onCompleted: (data) => {
      const { payRedirectUrl } = data?.magento2?.redirectAndPlaceOrder ?? {};

      if (payRedirectUrl) {
        setRedirecting(true);
        window.location.href = payRedirectUrl;
      }
    },
    onError: (error) => {
      setRedirecting(false);
      addAlerts([genericErrorAlert({ message: error.message })]);
    },
  });

  const [
    setPaymentMethodAndPlaceOrder,
    { loading: setMethodLoading },
  ] = useMutation<SetPaymentMethodMutation, SetPaymentMethodMutationVariables>(
    SET_PAYMENT_METHOD_MUTATION,
    {
      variables: { cartId: cartId ?? '', code: selectedMethodCode ?? '' },
      onCompleted: (data) => {
        const cartMethodCode =
          data?.magento2?.setPaymentMethodOnCart?.cart?.selectedPaymentMethod
            ?.code;

        if (cartMethodCode) placeOrder();
      },
      onError: (error) => {
        addAlerts([genericErrorAlert({ message: error.message })]);
      },
    }
  );

  const {
    availablePaymentMethods: cartAvailablePaymentMethods,
    selectedPaymentMethod: cartSelectedPaymentMethod,
  } = data?.magento2?.customerCart ?? {};

  const availablePaymentMethods = useMemo(
    () => cartAvailablePaymentMethods?.filter(isNotNullish),
    [cartAvailablePaymentMethods]
  );

  const cartSelectedMethodCode = cartSelectedPaymentMethod?.code;

  const { type: previousMethodCode } =
    data?.magento2?.customer?.globalOrders?.items?.[0]?.paymentMethods?.[0] ??
    {};

  const isValidMethodCode = useCallback(
    (code?: string): code is string =>
      !!availablePaymentMethods?.find((method) => method.code === code),
    [availablePaymentMethods]
  );

  const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    ({ target }) => {
      clearAlerts();
      setSelectedMethodCode(target.value);
    },
    [clearAlerts]
  );

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      clearAlerts();

      if (!cartId) throw new Error('Cannot submit payment without cart id');

      if (!selectedMethodCode) {
        addAlerts([
          genericErrorAlert({ message: <RequiredValidationError /> }),
        ]);

        return;
      }

      if (selectedMethodCode === cartSelectedMethodCode) placeOrder();
      else setPaymentMethodAndPlaceOrder();
    },
    [
      cartId,
      selectedMethodCode,
      cartSelectedMethodCode,
      setPaymentMethodAndPlaceOrder,
      placeOrder,
      addAlerts,
      clearAlerts,
    ]
  );

  useEffect(() => {
    setSelectedMethodCode((currentCode) => {
      const [validCurrentCode, validCartCode, validPreviousCode] = [
        currentCode,
        cartSelectedMethodCode,
        previousMethodCode,
      ].map((code) => (isValidMethodCode(code) ? code : undefined));

      return validCurrentCode || validCartCode || validPreviousCode;
    });
  }, [cartSelectedMethodCode, previousMethodCode, isValidMethodCode]);

  useEffect(() => {
    if (status === PLACE_ORDER_STATUS_FAILED) {
      addAlerts([
        {
          type: AlertType.Error,
          persist: true,
          content: (
            <>
              <AlertTitle>
                <FormattedMessage {...messages.paymentFailedErrorTitle} />
              </AlertTitle>
              <AlertMessage>
                <FormattedMessage {...messages.paymentFailedErrorMessage} />
              </AlertMessage>
            </>
          ),
        },
      ]);
    }
  }, [status, addAlerts]);

  const isLoading =
    methodsLoading || setMethodLoading || placeOrderLoading || redirecting;

  if (!cartId) return null;

  return (
    <>
      <H1>
        <FormattedMessage {...messages.title} />
      </H1>
      <Box mt={4}>
        {!!alerts.length && (
          <Box data-testid="alerts" mb={3}>
            <Alerts alerts={alerts} />
          </Box>
        )}
        <form onSubmit={handleSubmit}>
          {availablePaymentMethods?.map(({ code, title }) => (
            <Fragment key={code}>
              <RadioBoxRadio
                id={code}
                value={code}
                checked={code === selectedMethodCode}
                onChange={handleChange}
                disabled={isLoading}
                outlineChecked
              />
              <RadioBoxLabel
                data-testid={`payment-option-${code}`}
                htmlFor={code}
              >
                <PaymentOption code={code} title={title} />
              </RadioBoxLabel>
            </Fragment>
          ))}
          <Flex mt={4} justifyContent="space-between" alignItems="center">
            <CheckoutBackLink to="/:language/shop/checkout/review">
              <FormattedMessage {...messages.back} />
            </CheckoutBackLink>
            <Button
              type="primary"
              isLoading={isLoading}
              disabled={isLoading}
              onClick={handleSubmit}
              data-testid="place-order-button"
            >
              <FormattedMessage {...messages.continue} />
            </Button>
          </Flex>
        </form>
      </Box>
    </>
  );
};

export default Payment;

const genericErrorAlert = ({
  message,
  persist = false,
}: {
  message: ReactNode;
  persist?: boolean;
}) => ({
  persist,
  type: AlertType.Error,
  content: (
    <>
      <AlertTitle>
        <FormattedMessage {...messages.alertErrorTitle} />
      </AlertTitle>
      <AlertMessage>{message}</AlertMessage>
    </>
  ),
});

const RequiredValidationError: FC = () => (
  <FormattedMessage
    {...messages.requiredValidationError}
    values={{
      field: <FormattedMessage {...messages.paymentMethodRequiredError} />,
    }}
  />
);

export const PLACE_ORDER_STATUS_FAILED = 'failed';

export const getReturnUrl = (language: string): string =>
  `${APP_BASE_URL}/${language}/shop/checkout/payment/`;

export const getSuccessUrl = (language: string, cartId: string): string =>
  `${APP_BASE_URL}/${language}/shop/checkout/response/${cartId}/`;

export const getFailedUrl = (language: string): string =>
  `${getReturnUrl(language)}${PLACE_ORDER_STATUS_FAILED}/`;
